import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  TemplateRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Table, TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ToolbarModule } from 'primeng/toolbar';
import { PaginatorModule } from 'primeng/paginator';
import { ChipComponent } from '../chip/chip.component';
import { TooltipModule } from 'primeng/tooltip';

export type Column = { field: string; header: string; sortable?: boolean }[];

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ButtonModule,
    MenuModule,
    ToolbarModule,
    PaginatorModule,
    ChipComponent,
    TooltipModule
  ],
  templateUrl: './data-table.component.html',
})
export class DataTableComponent implements OnInit, OnDestroy {
  protected selected: any[] = [];
  @Input() columns: Column = [];
  @Input() data: any[] = [];
  @Input() filters: string[] = [];
  @Input() totalRecords: number = 0;
  @Input() loading: boolean = false;
  @Input() cardTemplate: TemplateRef<any> | null = null; // Template for Grid View

  @Output() add = new EventEmitter<void>();
  @Output() edit = new EventEmitter<any>();
  @Output() removeSelected = new EventEmitter<any[]>();
  @Output() pageChange = new EventEmitter<{ page: number; rows: number }>();
  @Output() search = new EventEmitter<string>();

  protected activeItem: any;
  protected menuItems: MenuItem[] = [];
  protected layout: 'list' | 'grid' = 'list'; // View state

  @ViewChild('table') table?: Table;

  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  ngOnInit() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(query => {
      this.search.emit(query);
    });
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onGlobalFilter(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  openNew() {
    this.add.emit();
  }

  onEdit(row: any) {
    this.edit.emit(row);
  }

  onRowClick(event: Event, row: any) {
    const target = event.target as HTMLElement;
    if (target.closest('.p-checkbox') || target.closest('.p-button')) {
      return;
    }
    this.onEdit(row);
  }

  deleteSelectedRows() {
    this.removeSelected.emit(this.selected);
    this.selected = [];
  }

  isCategoriesColumn(column: any): boolean {
    return column.field === 'categories';
  }

  isStatusColumn(column: any): boolean {
    return column.field === 'status';
  }

  onPage(event: any) {
    const page = event.page;
    const pageSize = event.rows || 10;
    this.pageChange.emit({ page, rows: pageSize });
  }
}
